<script setup lang="ts">
import SupportContactDialog from '@/components/SupportContactDialog.vue'
import { useCheckoutTrialWarning } from '@/composables/useCheckoutTrialWarning'
import { useShowPassengerTransportDocumentCreate } from '@/composables/useShowPassengerTransportDocumentCreate'
import { useStripeBillingEnabled } from '@/composables/useStripeBillingEnabled'
import i18n, { loadLocaleMessages, syncDocumentLang } from '@/i18n'
import { authService } from '@/services/AuthService'
import { useProfileStore } from '@/store/profile.module'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

const props = defineProps<{
  visible: boolean
  visibleMobile: boolean
}>()

const emit = defineEmits<{
  (e: 'toggle'): void
}>()

const router = useRouter()
const { t, locale } = useI18n()

function goToAccount() {
  router.push({ name: 'accountProfile' })
}

const expandedMenus = ref<{ [key: string]: boolean }>({})
const isMobile = ref(false)
const supportModalVisible = ref(false)

const languageOptions = computed(() => [
  { label: t('language.es'), value: 'es' },
  { label: t('language.en'), value: 'en' },
  { label: t('language.pt'), value: 'pt' },
  { label: t('language.fr'), value: 'fr' }
])

const currentLanguage = computed({
  get: () => locale.value,
  set: async (value: string) => {
    if (!value || value === locale.value) return

    // Load the locale messages if not already loaded
    if (!i18n.global.availableLocales.includes(value)) {
      await loadLocaleMessages(value)
    }

    locale.value = value
    localStorage.setItem('appLanguage', value)
    syncDocumentLang(value)
  }
})

// Detectar si está en un dispositivo móvil
const isMobileDevice = computed(() => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
})

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768
}

const profileStore = useProfileStore()

const { showPassengerTransportDocumentCreate } = useShowPassengerTransportDocumentCreate()
const { stripeBillingEnabled } = useStripeBillingEnabled()
const { showWarning: showCheckoutTrialWarning } = useCheckoutTrialWarning()

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
})

const toggleSubmenu = (menuKey: string) => {
  const isCurrentlyExpanded = expandedMenus.value[menuKey]

  // Close all other submenus
  Object.keys(expandedMenus.value).forEach(key => {
    if (key !== menuKey) {
      expandedMenus.value[key] = false
    }
  })

  // Toggle the clicked submenu
  expandedMenus.value[menuKey] = !isCurrentlyExpanded
}

const signOut = async () => {
  localStorage.clear();
  await authService.logout()
  router.replace('/login')
}

const openSupportModal = () => {
  supportModalVisible.value = true
}

const profile = computed(() => profileStore.profile)

const featureFlags = computed(() => {
  const companyId = profileStore.selectedCompany?.id
  if (!companyId) return null
  return profileStore.getCompanyFeatureFlags(companyId)
})

const userName = computed(() => {
  if (profile.value) return `${profile.value.name} ${profile.value.surname}`
  const current = authService.getCurrentUser()
  return current?.displayName || ''
})

const userEmail = computed(() => {
  if (profile.value) return profile.value.email
  const current = authService.getCurrentUser()
  return current?.email || ''
})

const menuItems = computed(() => {
  const items = [
    {
      key: 'home',
      label: t('menu.home'),
      icon: 'pi pi-home',
      route: '/'
    },
    {
      key: 'operations',
      label: t('menu.operations'),
      icon: 'pi pi-briefcase',
      submenu: [
        {
          key: 'deliveryNotes',
          label: t('deliveryNotes.title'),
          icon: 'pi pi-file',
          route: '/deliveryNotes'
        },
        ...(showPassengerTransportDocumentCreate.value ? [{
          key: 'passengerTransportDocuments',
          label: t('passengerTransportDocuments.title'),
          icon: 'pi pi-ticket',
          route: '/passengerTransportDocuments'
        }] : []),
        {
          key: 'loadOrders',
          label: t('loadOrders.title'),
          icon: 'pi pi-box',
          route: '/loadOrders'
        }
      ]
    },
    {
      key: 'resources',
      label: t('menu.resources'),
      icon: 'pi pi-folder',
      submenu: [
        {
          key: 'customers',
          label: t('customers.title'),
          icon: 'pi pi-users',
          route: '/customers'
        },
        {
          key: 'vehicles',
          label: t('vehicles.title'),
          icon: 'pi pi-truck',
          route: '/vehicles'
        },
        {
          key: 'places',
          label: t('places.title'),
          icon: 'pi pi-map-marker',
          route: '/places'
        },
        {
          key: 'payloads',
          label: t('payloads.title'),
          icon: 'pi pi-box',
          route: '/payloads'
        },
        {
          key: 'drivers',
          label: t('drivers.title'),
          icon: 'pi pi-user-plus',
          route: '/drivers'
        },
        {
          key: 'employees',
          label: t('employees.title'),
          icon: 'pi pi-user',
          route: '/employees'
        },

      ]
    },
    {
      key: 'documents',
      label: t('menu.documents'),
      icon: 'pi pi-file',
      submenu: [
        {
          key: 'vehicleDocuments',
          label: t('vehicleDocuments.title'),
          icon: 'pi pi-truck',
          route: '/vehicle-documents'
        },
        {
          key: 'employeeDocuments',
          label: t('employeeDocuments.title'),
          icon: 'pi pi-user',
          route: '/employee-documents'
        }
      ]
    },
    {
      key: 'billing',
      label: t('menu.billing'),
      icon: 'pi pi-money-bill',
      submenu: [
        {
          key: 'prices',
          label: t('prices.title'),
          icon: 'pi pi-euro',
          route: '/prices'
        },
        {
          key: 'bills',
          label: t('bills.title'),
          icon: 'pi pi-money-bill',
          route: '/bills'
        },
        {
          key: 'expenses',
          label: t('expenses.title'),
          icon: 'pi pi-dollar',
          route: '/expenses'
        }
      ]
    },
    {
      key: 'reports',
      label: t('menu.reports'),
      icon: 'pi pi-chart-line',
      submenu: [
        {
          key: 'dashboard',
          label: t('dashboard.subtitle'),
          icon: 'pi pi-th-large',
          route: '/general-dashboard'
        },
        {
          key: 'production',
          label: t('menu.production'),
          icon: 'pi pi-chart-bar',
          route: '/production'
        },
        {
          key: 'billingDashboard',
          label: t('billingDashboard.title'),
          icon: 'pi pi-chart-pie',
          route: '/billing-dashboard'
        }
      ]
    },
    {
      key: 'tools',
      label: t('menu.tools'),
      icon: 'pi pi-wrench',
      submenu: [
        {
          key: 'refuels',
          label: t('refuels.title'),
          icon: 'pi pi-gauge',
          route: '/refuels'
        },
      ]
    },
    {
      key: 'config',
      label: t('menu.config'),
      icon: 'pi pi-cog',
      route: '/profile/deliveryNotesConfig'
    },
    ...(stripeBillingEnabled.value ? [{
      key: 'subscription',
      label: t('menu.subscription'),
      icon: 'pi pi-credit-card',
      route: '/account/subscription'
    }] : [])
  ]

  // move to the tools menu
  const toolsMenu = items.find((item) => item.key === 'tools')
  if (featureFlags.value?.showDeliveryNoteParserInWeb) {
    toolsMenu?.submenu?.push({
      key: 'deliveryNoteParser',
      label: t('menu.deliveryNoteParser'),
      icon: 'pi pi-file-import',
      route: '/delivery-note-parser'
    })
  }
  

  return items
})


// role display removed per request
</script>

<template>
  <div class="layout-sidebar" :class="{
    'layout-sidebar-collapsed': !visible && !isMobile,
    'layout-sidebar-mobile': isMobile,
    'layout-sidebar-mobile-visible': visibleMobile
  }">
    <div class="sidebar-header" v-if="!isMobile">
      <div class="flex align-items-center justify-content-between">
        <Image v-if="visible || isMobile" src="/logo.png" alt="logo" height="40" style="cursor: pointer"
          @click="router.push('/')" />
        <Button :icon="visible ? 'pi pi-chevron-left' : 'pi pi-chevron-right'" text rounded @click="emit('toggle')"
          class="toggle-button" :class="{ 'toggle-button-collapsed': !visible }" />
      </div>
    </div>

    <div class="layout-menu">
      <div v-for="item in menuItems" :key="item.key" class="menu-item">
        <div v-if="item.submenu" class="menu-header" :class="{ 'menu-header-expanded': expandedMenus[item.key] }"
          @click="toggleSubmenu(item.key)">
          <i :class="item.icon"></i>
          <span v-if="visible || isMobile">{{ item.label }}</span>
          <i :class="[
            'submenu-icon',
            visible || isMobile ? 'pi pi-chevron-down' : 'pi pi-chevron-right',
            { 'submenu-icon-expanded': expandedMenus[item.key] }
          ]"></i>
        </div>
        <div
          v-else
          class="menu-header"
          :class="{
            'menu-header--subscription-warning':
              item.key === 'subscription' && showCheckoutTrialWarning
          }"
          @click="router.push(item.route)"
        >
          <i :class="item.icon"></i>
          <span v-if="visible || isMobile">{{ item.label }}</span>
          <i
            v-if="item.key === 'subscription' && showCheckoutTrialWarning"
            class="pi pi-exclamation-triangle subscription-trial-warning-icon"
            aria-hidden="true"
          ></i>
        </div>

        <div v-if="item.submenu && expandedMenus[item.key]" class="submenu"
          :class="{ 'submenu-collapsed': !visible && !isMobile }">
          <div v-for="subItem in item.submenu" :key="subItem.key" class="submenu-item"
            @click="router.push(subItem.route)">
            <i :class="subItem.icon"></i>
            <span v-if="visible || isMobile">{{ subItem.label }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Language selector -->
    <div class="language-wrapper">
      <div class="language-select">
        <i class="pi pi-globe language-icon"></i>
        <Select v-model="currentLanguage" :options="languageOptions" optionLabel="label" optionValue="value"
          class="language-dropdown" />
      </div>
    </div>

    <!-- Documentation button -->
    <div class="documentation-wrapper">
      <Button
        class="documentationButton w-full mb-2"
        :label="visible || isMobile ? $t('menu.documentation') : ''"
        icon="pi pi-book"
        iconPos="left"
        link
        @click="router.push('/guides')"
      />
    </div>

    <!-- Support button moved above the footer separator -->
    <div class="support-wrapper">
      <Button class="supportButton w-full mb-2" :label="visible || isMobile ? $t('menu.support') : ''"
        icon="pi pi-comments" iconPos="left" link @click="openSupportModal" />
    </div>

    <div class="sidebar-footer">
      <div class="user-card" @click="goToAccount">
        <div class="user-left">
          <div class="user-avatar">{{ (userName || userEmail) ? (userName ? userName.charAt(0) : userEmail.charAt(0)) :
            'P' }}</div>
          <div class="user-details">
            <div class="user-name" v-if="visible || isMobile">{{ userName || userEmail }}</div>
            <div class="user-email" v-if="visible || isMobile">{{ userEmail }}</div>
          </div>
        </div>
      </div>

      <div class="signout-row">
        <Button class="signOutButton" :label="visible || isMobile ? $t('login.signOut') : ''" icon="pi pi-sign-out"
          iconPos="right" link @click="signOut" />
      </div>
    </div>
  </div>

  <SupportContactDialog v-model:visible="supportModalVisible" />
</template>

<style scoped>
.layout-sidebar {
  position: fixed;
  width: 280px;
  height: 100vh;
  z-index: 999;
  overflow-y: auto;
  background-color: var(--p-content-background);
  border-right: 1px solid var(--p-content-border-color);
  display: flex;
  flex-direction: column;
  transition: all 0.3s;
  left: 0;
  top: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.layout-sidebar-collapsed {
  width: 4rem;
}

.layout-sidebar-mobile {
  transform: translateX(-100%);
  width: 280px;
  visibility: hidden;
}

.layout-sidebar-mobile-visible {
  transform: translateX(0);
  visibility: visible;
}

.sidebar-header {
  padding: 1.5rem;
  border-bottom: 1px solid var(--p-content-border-color);
  background-color: var(--p-content-background);
}

.layout-menu {
  flex: 1;
  padding: 1rem 0;
}

.sidebar-footer {
  padding: 1rem;
  border-top: 1px solid var(--p-content-border-color);
  background-color: var(--p-content-background);
}

.menu-item {
  margin-bottom: 0.25rem;
}

.menu-header {
  padding: 0.75rem 1.25rem;
  color: var(--p-text-muted-color);
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
  font-size: 0.95rem;
  border-radius: var(--p-content-border-radius);
}

.menu-header:hover {
  background-color: var(--p-content-hover-background);
  color: var(--p-text-color);
}

.menu-header i:first-child {
  margin-right: 0.75rem;
  font-size: 1.1rem;
  transition: color 0.2s;
}

.menu-header:hover i:first-child {
  color: var(--p-text-color);
}

.menu-header--subscription-warning {
  position: relative;
}

.subscription-trial-warning-icon {
  margin-left: auto;
  flex-shrink: 0;
  font-size: 0.85rem;
  color: var(--p-yellow-500, #eab308);
}

.menu-header:hover .subscription-trial-warning-icon {
  color: var(--p-yellow-600, #ca8a04);
}

.layout-sidebar-collapsed .menu-header--subscription-warning .subscription-trial-warning-icon {
  margin-left: 0;
  position: absolute;
  top: 0.35rem;
  right: 0.35rem;
  font-size: 0.65rem;
}

.submenu-icon {
  margin-left: auto;
  transition: transform 0.2s;
  font-size: 0.8rem;
}

.submenu-icon-expanded {
  transform: rotate(180deg);
}

.layout-sidebar-collapsed .submenu-icon {
  display: block;
  margin-left: 0;
  margin-top: 0.25rem;
}

.submenu {
  background-color: var(--p-content-background);
  transition: all 0.2s;
}

.submenu-item {
  padding: 0.75rem 1.25rem 0.75rem 2.5rem;
  color: var(--p-text-muted-color);
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.9rem;
  border-radius: var(--p-content-border-radius);
}

.submenu-item:hover {
  background-color: var(--p-content-hover-background);
  color: var(--p-text-color);
}

.submenu-item i {
  margin-right: 0.75rem;
  font-size: 1.1rem;
  transition: color 0.2s;
}

.submenu-item:hover i {
  color: var(--p-text-color);
}

.submenu-collapsed .submenu-item {
  padding: 0.75rem;
  justify-content: center;
}

.submenu-collapsed .submenu-item i {
  margin-right: 0;
}

.layout-sidebar-collapsed .menu-header {
  padding: 0.75rem;
  justify-content: center;
}

.layout-sidebar-collapsed .menu-header i:first-child {
  margin-right: 0;
}

.layout-sidebar-collapsed .submenu-icon {
  display: none;
}

.supportButton,
.documentationButton {
  color: var(--p-text-muted-color);
  width: 100%;
  justify-content: flex-start;
  padding: 0.6rem 1rem;
  font-size: 0.92rem;
  transition: all 0.2s;
}

.layout-sidebar-collapsed .supportButton {
  justify-content: center;
  padding: 0.6rem;
}

.supportButton:hover {
  background-color: var(--p-content-hover-background);
  color: var(--p-text-color);
}

.supportButton .p-button-icon {
  font-size: 1.1rem;
  transition: color 0.2s;
}

.language-wrapper {
  padding: 0 0.75rem 0.75rem 0.75rem;
}

.language-select {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.language-icon {
  font-size: 1.1rem;
  color: var(--p-text-muted-color);
}

.language-dropdown {
  width: 100%;
}

.layout-sidebar-collapsed .language-wrapper {
  display: none;
}

.supportButton:hover .p-button-icon {
  color: var(--p-text-color);
}

.signOutButton {
  color: var(--p-red-500);
  min-width: 44px;
  justify-content: flex-start;
  padding: 0.45rem 0.75rem;
  font-size: 0.88rem;
  transition: all 0.2s;
}

.layout-sidebar-collapsed .signOutButton {
  justify-content: center;
  padding: 0.45rem;
}

.signOutButton:deep(.p-button-label),
.signOutButton:deep(.p-button-icon) {
  color: var(--p-red-500);
  transition: color 0.2s;
}

.signOutButton:deep(.p-button-icon) {
  font-size: 1.1rem;
}

.signOutButton:hover,
.signOutButton:not(:disabled):hover {
  background-color: var(--p-red-50) !important;
  color: var(--p-red-600) !important;
}

.signOutButton:hover:deep(.p-button-label),
.signOutButton:hover:deep(.p-button-icon),
.signOutButton:not(:disabled):hover:deep(.p-button-label),
.signOutButton:not(:disabled):hover:deep(.p-button-icon) {
  color: var(--p-red-600) !important;
}

/* ensure details can shrink so the sign-out button remains visible */
.user-details {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
}

/* Ensure signOut stays visible and aligned inside user-card */
.user-card .signOutButton {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
}

.user-card .signOutButton:deep(.p-button-icon) {
  margin-left: 0.4rem;
}

.user-card {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.75rem;
  border-radius: 8px;
  margin-bottom: 0.25rem;
  background-color: transparent;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.user-card:hover {
  background-color: var(--p-content-hover-background, #f8f9fa);
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #22303c;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  overflow: hidden;
  flex-shrink: 0;
  font-size: 0.95rem;
  text-transform: uppercase;
}

.user-left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.user-details {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
}

.user-signout {
  display: none;
}

.signout-row {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.75rem 0.25rem 0.75rem;
}

.user-name {
  font-weight: 600;
  color: var(--p-text-color);
  font-size: 0.9rem;
}

.user-email {
  font-size: 0.78rem;
  color: var(--p-text-muted-color);
}

/* role removed - styles not needed */

@media screen and (max-width: 768px) {
  .layout-sidebar {
    top: 4rem;
    height: calc(100vh - 4rem);
    visibility: visible;
  }
}

/* Collapsed sidebar adjustments: center avatar and sign-out */
.layout-sidebar-collapsed .user-card {
  flex-direction: column;
  align-items: center;
  padding: 0.35rem 0.25rem;
}

.layout-sidebar-collapsed .user-left {
  gap: 0;
  flex-direction: column;
  align-items: center;
}

.layout-sidebar-collapsed .user-avatar {
  width: 34px;
  height: 34px;
}

.layout-sidebar-collapsed .signout-row {
  justify-content: center;
  padding: 0.25rem 0;
}

.layout-sidebar-collapsed .signOutButton {
  padding: 0.35rem;
  min-width: 36px;
}

.layout-sidebar-collapsed .supportButton {
  justify-content: center;
  padding: 0.5rem;
}
</style>
